import { useCallback, useMemo } from "react"
import { useSearchParams } from "react-router-dom"

/**
 * Hook that syncs filter state with URL search params.
 * Each filter key maps to a URL param. Empty values are removed from the URL.
 */
export function useFilterParams<T extends Record<string, string>>(
  filterKeys: readonly (keyof T & string)[]
) {
  const [searchParams, setSearchParams] = useSearchParams()

  const filters = useMemo(() => {
    const result: Record<string, string> = {}
    for (const key of filterKeys) {
      const value = searchParams.get(key)
      if (value) {
        result[key] = value
      }
    }
    return result as Partial<T>
  }, [searchParams, filterKeys])

  const setFilter = useCallback(
    (key: keyof T & string, value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (value) {
            next.set(key, value)
          } else {
            next.delete(key)
          }
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setFilters = useCallback(
    (updates: Partial<T>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          for (const [key, value] of Object.entries(updates)) {
            if (value) {
              next.set(key, value as string)
            } else {
              next.delete(key)
            }
          }
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const clearFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const key of filterKeys) {
          next.delete(key)
        }
        return next
      },
      { replace: true }
    )
  }, [setSearchParams, filterKeys])

  const activeFilterCount = useMemo(() => {
    let count = 0
    for (const key of filterKeys) {
      if (searchParams.get(key)) count++
    }
    return count
  }, [searchParams, filterKeys])

  return {
    filters,
    setFilter,
    setFilters,
    clearFilters,
    activeFilterCount,
  }
}
